import { Table, Text } from 'octiv-components';
import { AccessContext } from 'octiv-context';
import { useActiveUserTenant } from 'octiv-context/ActiveUserTenant';
import { useMemberModal } from 'octiv-context/MemberModal';
import {
  canSetMemberModal,
  getDateReadableDayMonthYear,
  getFullName,
  toCapital,
} from 'octiv-utilities/Functions';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export default ({ data, onClickAction, ...props }) => {
  const { t } = useTranslation();

  const hasAccess = useContext(AccessContext);
  const { setMemberModal } = useMemberModal();
  const { toCurrency } = useActiveUserTenant();

  const tableData = useMemo(() => data || [], [data]);
  const tableColumns = useMemo(
    () => [
      {
        Header: t('date'),
        accessor: 'invoice.dueOn',
        sortBy: 'due_on',
        Cell: ({ value }) => getDateReadableDayMonthYear({ date: value }),
      },
      {
        Header: t('invoiceCode'),
        accessor: 'invoice',
        sortBy: 'code',
        Cell: ({ value }) => (
          <Text
            as='span'
            onClick={() =>
              window.open(`/accounts/invoices?invoiceId=${value?.id}`)
            }
          >
            {value?.code}
          </Text>
        ),
      },
      {
        Header: t('member'),
        Cell: ({
          row: {
            original: { invoice },
          },
        }) => (
          <Text
            as='span'
            onClick={
              canSetMemberModal({
                userTenant: invoice.user?.userTenant,
                hasAccess,
              })
                ? (e) => {
                    e.stopPropagation();
                    setMemberModal({
                      userTenantId: invoice.user?.userTenant?.id,
                    });
                  }
                : null
            }
          >
            {getFullName(
              invoice.user || invoice.leadMember || invoice.nonMember
            )}
          </Text>
        ),
      },
      {
        Header: t('description'),
        accessor: 'description',
        sortBy: 'description',
        whiteSpaceWrap: true,
      },
      {
        Header: t('quantity'),
        accessor: 'quantity',
        sortBy: 'quantity',
      },
      {
        Header: t('status'),
        accessor: 'invoice.status',
        sortBy: 'status',
        Cell: ({ value }) => (
          <Text
            as='span'
            color={
              value === 'pending' || value === 'submitted'
                ? 'warning'
                : value === 'credited' || value === 'refunded'
                ? 'info'
                : value === 'paid'
                ? 'success'
                : value === 'unpaid'
                ? 'danger'
                : undefined
            }
          >
            {toCapital({ value: t(value) })}
          </Text>
        ),
      },
      {
        Header: t('subTotal'),
        accessor: 'exVatAmount',
        Cell: ({ value }) => toCurrency({ value }),
      },
      {
        Header: t('vatAmount'),
        accessor: 'vatAmount',
        Cell: ({ value }) => toCurrency({ value }),
      },
      {
        Header: `${t('vat')} %`,
        accessor: 'vat',
      },
      {
        Header: t('total'),
        accessor: 'amount',
        sortBy: 'amount',
        Cell: ({ value }) => toCurrency({ value }),
      },
    ],
    [toCurrency, setMemberModal, hasAccess, t]
  );

  const sortBy = useMemo(
    () => [
      {
        id: 'invoice.dueOn',
        desc: true,
      },
    ],
    []
  );

  return (
    <Table {...props} columns={tableColumns} data={tableData} sortBy={sortBy} />
  );
};
